<template>
  <div>
    <FlexLayout col :gap="12">
      <GrayBlockContainer v-for="signature in displaySignatureList" :key="signature.name" class="w-full">
        <FlexLayout col :gap="12">
          <FlexLayout align="center" :gap="20">
            <p class="label">簽名同意書</p>
            <p>{{ signature.name }}</p>
          </FlexLayout>
          <FlexLayout align="center" :gap="20">
            <p class="label">簽署狀況</p>
            <FlexLayout align="center" :gap="20">
              <Tag :type="signature.tagType">{{ signature.status }}</Tag>
              <BaseElButton
                v-if="signature.status !== '無'"
                type="text"
                class="underline text-primary-100"
                @click="openSignatureDialog(signature)"
              >
                檢視
              </BaseElButton>
            </FlexLayout>
          </FlexLayout>
          <FlexLayout align="start" :gap="20">
            <p class="label">適用範圍</p>
            <div>
              <FlexLayout col :gap="4">
                <!-- <div v-if="!signature.enableApplyScope" class="text-normal text-gray-60">
                  適用所有服務
                </div> -->
                <div v-if="signature.applyScopeItems && signature.applyScopeItems.length > 0">
                  <p v-for="(item, itemIdx) in signature.applyScopeItems" :key="itemIdx" class="text-normal text-gray-60">
                    {{ item }}
                  </p>
                </div>
              </FlexLayout>
            </div>
          </FlexLayout>
        </FlexLayout>
      </GrayBlockContainer>
    </FlexLayout>

    <!-- 簽名檢視對話框 -->
    <BaseDialog
      v-if="showSignatureDialog"
      title="檢視簽名"
      width="570px"
      @close="closeSignatureDialog"
    >
      <div v-if="displayDialogData" style="margin-bottom: 40px;">
        <FlexLayout col :gap="20">
          <FlexLayout col :gap="8" class="text-normal">
            <FlexLayout align="center" :gap="20">
              <span class="text-gray-60" style="width: 128px;">簽名同意書</span>
              <span class="text-gray-100">{{ displayDialogData.name }}</span>
            </FlexLayout>
            <FlexLayout align="center" :gap="20">
              <span class="text-gray-60" style="width: 128px;">簽名建立時間</span>
              <span class="text-gray-100">{{ displayDialogData.createdAt }}</span>
            </FlexLayout>
            <FlexLayout align="center" :gap="20">
              <span class="text-gray-60" style="width: 128px;">簽名生效時間</span>
              <span class="text-gray-100">{{ displayDialogData.effectiveAt }}</span>
            </FlexLayout>
            <FlexLayout v-if="displayDialogData.voidedAt" align="center" :gap="20">
              <span class="text-gray-60" style="width: 128px;">簽名作廢時間</span>
              <span class="text-gray-100">{{ displayDialogData.voidedAt }}</span>
            </FlexLayout>
            <FlexLayout v-if="displayDialogData.originOrder.orderCode" align="center" :gap="20">
              <span class="text-gray-60" style="width: 128px;">簽名來源</span>
              <span class="underline text-primary-100 cursor-pointer" @click="goOrder">{{ displayDialogData.originOrder.orderCode }}</span>
            </FlexLayout>
          </FlexLayout>

          <!-- 簽名圖片 -->
          <div v-if="selectedSignature && selectedSignature.signatureImagePath">
            <FlexLayout
              v-loading="loadingImage"
              class="bg-primary-10 m-auto relative"
              align="center"
              justify="center"
              style="width: 343px; border-radius: 16px;"
            >
              <img
                v-if="signatureImageUrl && !loadingImage"
                :src="signatureImageUrl"
                alt="簽名圖片"
                class="w-full h-full"
                :class="{ 'opacity-50': selectedSignature.voidedAt }"
              >
              <!-- 作廢浮水印 -->
              <div
                v-if="selectedSignature.voidedAt && signatureImageUrl && !loadingImage"
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div class="signature-void-watermark">
                  簽名作廢
                </div>
              </div>
            </FlexLayout>
          </div>
        </FlexLayout>
      </div>
    </BaseDialog>
  </div>
</template>

<script>
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import Tag from '@/components/Tag/Tag.vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { get, map } from 'lodash'
import { formatDate } from '@/utils/date'
import { GetS3FileUrl } from '@/api/file'

import { computed, defineComponent, inject, ref } from 'vue'
import { useShop } from '@/use/shop'

export default defineComponent({
  name: 'SignatureDetailBlock',
  components: {
    GrayBlockContainer,
    Tag,
    BaseDialog,
  },
  setup (props, { emit }) {
    const reservationData = inject('reservation')
    const { shopId } = useShop()
    const showSignatureDialog = ref(false)
    const selectedSignature = ref(null)
    const signatureImageUrl = ref('')
    const loadingImage = ref(false)

    const openSignatureDialog = async (signature) => {
      signatureImageUrl.value = ''
      loadingImage.value = false
      selectedSignature.value = signature
      showSignatureDialog.value = true

      // 如果有 SignatureImage，獲取圖片 URL
      if (signature.signatureImagePath) {
        loadingImage.value = true
        const [res, errMsg] = await GetS3FileUrl({ shopId: shopId.value, path: signature.signatureImagePath })
        loadingImage.value = false

        if (errMsg) {
          window.$message.error(errMsg)
          return
        }

        signatureImageUrl.value = get(res, 'url')
      }
    }

    const closeSignatureDialog = () => {
      showSignatureDialog.value = false
      selectedSignature.value = null
      signatureImageUrl.value = ''
      loadingImage.value = false
    }

    const formatExpirationPeriod = (Signature) => {
      const expirationMode = get(Signature, 'expirationMode')
      const expirationPeriodSetting = get(Signature, 'expirationPeriodSetting')

      if (expirationMode === 'none') return '無期限'
      if (expirationMode === 'onetime') return '單次使用'

      if (!expirationPeriodSetting) return '-'

      const { unit, value } = expirationPeriodSetting
      const unitMap = {
        day: '天',
        month: '個月',
        year: '年',
      }
      const unitText = unitMap[unit] || unit
      if (Signature.expirationAt) {
        return `${value} ${unitText} (${formatDate(Signature.expirationAt, 'YYYY/MM/DD')})`
      }
      return `${value} ${unitText}`
    }

    const displayDialogData = computed(() => {
      if (!selectedSignature.value) return null

      const expirationPeriod = formatExpirationPeriod(selectedSignature.value)
      const expirationAt = selectedSignature.value.expirationAt ? formatDate(selectedSignature.value.expirationAt, 'YYYY/MM/DD HH:mm') : '-'
      // const combinedExpiration = `${expirationPeriod} ${expirationAt}`

      return {
        name: selectedSignature.value.name,
        createdAt: selectedSignature.value.createdAt ? formatDate(selectedSignature.value.createdAt, 'YYYY/MM/DD HH:mm') : '-',
        effectiveAt: selectedSignature.value.effectiveAt ? formatDate(selectedSignature.value.effectiveAt, 'YYYY/MM/DD HH:mm') : '-',
        expirationAt: expirationAt,
        expirationPeriod,
        voidedAt: selectedSignature.value.voidedAt ? formatDate(selectedSignature.value.voidedAt, 'YYYY/MM/DD HH:mm') : null,
        originOrder: {
          orderCode: get(selectedSignature.value, 'originOrder.code'),
          orderId: get(selectedSignature.value, 'originOrder.id'),
        },
      }
    })

    const displaySignatureList = computed(() => {
      const signatureMappings = reservationData.value?.AppointmentOrder?.SignatureRecordMappings || []
      if (!signatureMappings.length) return []

      // 以 SignatureTemplate.id 為 key 合併重複 mapping
      const groups = {}
      signatureMappings.forEach((mapping) => {
        const templateId = get(mapping, 'SignatureTemplate.id') || get(mapping, 'SignatureTemplate.name')
        if (!groups[templateId]) groups[templateId] = []
        groups[templateId].push(mapping)
      })

      return map(groups, (mappings) => {
        // mappings 為同一 template 的多筆 mapping
        // 優先判斷已作廢，其次有圖片，否則為無
        let status = '無'
        let tagType = 'danger'
        let createdAt = null
        let effectiveAt = null
        let voidedAt = null
        let signatureImagePath = null
        let expirationMode = null
        let expirationPeriodSetting = null
        let name = null
        let originOrder = null
        let enableApplyScope = false
        let applyScopeItems = []

        mappings.forEach((mapping) => {
          const record = get(mapping, 'SignatureRecord')
          const template = get(mapping, 'SignatureTemplate')
          const image = get(record, 'SignatureImage')
          const vAt = get(record, 'voidedAt')

          // set name / template related once
          if (!name && template) name = get(template, 'name')
          if (!expirationMode && template) expirationMode = get(template, 'expirationMode')
          if (!expirationPeriodSetting && template) expirationPeriodSetting = get(template, 'expirationPeriodSetting')
          if (!enableApplyScope && template) enableApplyScope = get(template, 'enableApplyScope', false)

          // createdAt: 取最早
          const cAt = get(record, 'createdAt')
          if (cAt && (!createdAt || new Date(cAt) < new Date(createdAt))) createdAt = cAt

          // effectiveAt: 取最近一個非空
          const eAt = get(record, 'effectiveAt')
          if (eAt && (!effectiveAt || new Date(eAt) > new Date(effectiveAt))) effectiveAt = eAt

          // voidedAt: 取最近一個非空
          if (vAt && (!voidedAt || new Date(vAt) > new Date(voidedAt))) voidedAt = vAt

          // signature image path: 取第一個有值的
          const s3 = get(record, 'SignatureImage.s3Path')
          if (!signatureImagePath && s3) signatureImagePath = s3

          // originOrder: 取第一個有的
          if (!originOrder && get(mapping, 'AppointmentOrder')) originOrder = get(mapping, 'AppointmentOrder')

          // status 決定（已作廢優先）
          if (vAt) {
            status = '已作廢'
            tagType = 'info'
          } else if (image && status !== '已作廢') {
            status = '有'
            tagType = 'action'
          }
        })

        // 處理適用服務項目
        if (enableApplyScope) {
          const applyScopeRecords = get(mappings[0], 'applyScopeRecords', [])
          applyScopeItems = applyScopeRecords.map(item => {
            const typeMap = {
              appointmentCategory: '服務類別',
              appointmentService: '服務項目',
              appointmentServiceAttach: '服務附加項目',
              appointmentSubService: '子服務項目',
            }
            return `${typeMap[item.type] || item.type} ${item.name}`
          })
        } else {
          applyScopeItems = ['適用所有服務']
        }

        return {
          name,
          status,
          tagType,
          createdAt,
          effectiveAt,
          expirationAt: get(mappings[0], 'SignatureRecord.expirationAt'),
          voidedAt,
          signatureImagePath,
          expirationPeriodSetting,
          expirationMode,
          originOrder,
          enableApplyScope,
          applyScopeItems,
        }
      })
    })

    const goOrder = () => {
      showSignatureDialog.value = false
      emit('goOrder', displayDialogData.value.originOrder.orderCode)
    }

    return {
      goOrder,
      displaySignatureList,
      displayDialogData,
      showSignatureDialog,
      selectedSignature,
      signatureImageUrl,
      loadingImage,
      openSignatureDialog,
      closeSignatureDialog,
      formatDate,
      formatExpirationPeriod,
      reservationData,
    }
  },
})
</script>

<style lang="postcss" scoped>
.label {
  @apply text-gray-60 w-[128px];
}

.signature-void-watermark {
  @apply text-red-500 text-3xl font-bold;
  background: rgba(255, 255, 255, 0.8);
  padding: 12px 24px;
  border-radius: 8px;
  transform: rotate(-15deg);
  border: 2px solid #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}
</style>
